import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import NavigationDropdown from "../../components/NavigationDropdown";
import NavigationPanel from "../../components/NavigationPanel";
import PortalPopup from "../../components/PortalPopup";
import LogoutConfirmation from "../../components/LogoutConfirmation";
import DownloadModalTables from "../../components/DownloadModalTables";
import DateFilter from "../../components/DateFilter";
import LawFirm from "../../components/LawFirm";
import Upload from "../../components/Upload";
import Footer from "../../components/Footer.js";
import { getCurrentUser } from "../../utils/getCurrentUser";
import { debtorDetailsService } from "../../services/debtorDetailsService";
import { documentService } from "../../services/documentService.js";

// ─── Shared invoice picker ────────────────────────────────────────────────────
const InvoicePickerTab = ({
  invoices, isLoading, searchQuery, onSearchChange,
  selectedInvoice, onSelect, emptyMessage, emptyHint,
  formatCurrency, formatDisplayDate,
}) => {
  const getId = (inv) => inv?._id || inv?.id;

  const filtered = (invoices || []).filter((inv) => {
    const q = (searchQuery || "").toLowerCase();
    return (
      (inv.xeroInvoiceNumber || inv.fileName || "").toLowerCase().includes(q) ||
      (inv.xeroContactName   || "").toLowerCase().includes(q)
    );
  });

  if (isLoading) {
    return (
      <div style={{ display:"flex", alignItems:"center", justifyContent:"center", padding:"32px 0", gap:10, color:"#64748B", fontSize:14 }}>
        <div className="lba-spinner" /> Loading invoices…
      </div>
    );
  }

  if (!invoices || invoices.length === 0) {
    return (
      <div style={{ textAlign:"center", padding:"32px 0", color:"#94A3B8" }}>
        <div style={{ fontSize:32, marginBottom:10 }}>📄</div>
        <div style={{ fontSize:14, fontWeight:600, color:"#64748B" }}>{emptyMessage}</div>
        <div style={{ fontSize:13, marginTop:4 }}>{emptyHint}</div>
      </div>
    );
  }

  return (
    <>
      <div className="lba-xero-search">
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path d="M11 19a8 8 0 100-16 8 8 0 000 16zM21 21l-4.35-4.35" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <input
          type="text"
          placeholder="Search by invoice number or contact…"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
        />
        {searchQuery && (
          <button onClick={() => onSearchChange("")} style={{ background:"none", border:"none", cursor:"pointer", padding:0, color:"#94A3B8", display:"flex" }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
      </div>

      <div style={{ maxHeight:280, overflowY:"auto" }}>
        {filtered.length === 0 ? (
          <div style={{ textAlign:"center", padding:"20px 0", color:"#94A3B8", fontSize:14 }}>No invoices match your search.</div>
        ) : (
          filtered.map((invoice) => {
            const id       = getId(invoice);
            const selected = getId(selectedInvoice) === id;
            const label    = invoice.xeroInvoiceNumber || invoice.fileName || "No Number";
            return (
              <div
                key={id}
                className={`lba-invoice-item${selected ? " selected" : ""}`}
                onClick={() => onSelect(invoice)}
              >
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:700, fontSize:14, color:"#0F172A", marginBottom:2 }}>{label}</div>
                    <div style={{ fontSize:13, color:"#64748B" }}>
                      {invoice.xeroContactName || (invoice._isUploaded ? "Manually uploaded" : "—")}
                    </div>
                    <div style={{ fontSize:12, color:"#94A3B8", marginTop:2 }}>
                      {formatCurrency(invoice.amountDue, invoice.currency)} · Due {formatDisplayDate(invoice.dueDate)}
                    </div>
                  </div>
                  {selected && (
                    <div style={{ width:20, height:20, background:"#2563EB", borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, marginLeft:10 }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                        <path d="M5 13l4 4L19 7" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </>
  );
};

// ─── Source tabs ──────────────────────────────────────────────────────────────
const SOURCE_TABS = [
  { key: "xero",       label: "Xero"        },
  { key: "sage",       label: "Sage"        },
  { key: "quickbooks", label: "QuickBooks"  },
  { key: "uploaded",   label: "Uploaded"    },
  { key: "manual",     label: "Manual"      },
];

// ─── Main component ───────────────────────────────────────────────────────────
const LBA = () => {
  const navigate = useNavigate();
  const user     = getCurrentUser();          // read once at top level
  const API_URL  = process.env.REACT_APP_API_URL;

  // helpers — never depend on state, always work
  const getToken      = ()  => localStorage.getItem("authToken");
  const getCompanyId  = ()  => user?.companyId || user?.id;

  // Navigation / modal visibility
  const [isNavigationDropdownOpen,  setNavigationDropdownOpen]  = useState(false);
  const [isLogoutConfirmationOpen,  setLogoutConfirmationOpen]  = useState(false);
  const [isDownloadModalTablesOpen, setDownloadModalTablesOpen] = useState(false);
  const [isDateFilterOpen,          setDateFilterOpen]          = useState(false);
  const [isLawFirmOpen,             setLawFirmOpen]             = useState(false);

  // User / company (kept in state for downstream uses)
  const [companyId, setCompanyId] = useState(null);
  const [userName,  setUserName]  = useState(null);

  // Table
  const [searchQuery,           setSearchQuery]           = useState("");
  const [totalSubmitted,        setTotalSubmitted]        = useState(0);
  const [lbaDetails,            setLbaDetails]            = useState([]);
  const [currentPage,           setCurrentPage]           = useState(1);
  const [selectedDateTimeRange, setSelectedDateTimeRange] = useState("");
  const [filterStatus,          setFilterStatus]          = useState("all");
  const itemsPerPage = 10;

  // Modal
  const [isUploadModalOpen,    setUploadModalOpen]    = useState(false);
  const [selectedFiles,        setSelectedFiles]       = useState([]);
  const [isUploading,          setIsUploading]         = useState(false);
  const [invoiceSource,        setInvoiceSource]       = useState("xero");
  const [invoiceSearchQuery,   setInvoiceSearchQuery]  = useState("");
  const [selectedInvoice,      setSelectedInvoice]     = useState(null);

  // Per-source invoice lists
  const [xeroInvoices,      setXeroInvoices]      = useState([]);
  const [isLoadingXero,     setIsLoadingXero]      = useState(false);
  const [sageInvoices,      setSageInvoices]       = useState([]);
  const [isLoadingSage,     setIsLoadingSage]      = useState(false);
  const [qbInvoices,        setQbInvoices]         = useState([]);
  const [isLoadingQb,       setIsLoadingQb]        = useState(false);
  const [uploadedInvoices,  setUploadedInvoices]   = useState([]);
  const [isLoadingUploaded, setIsLoadingUploaded]  = useState(false);

  // Law firm
  const [selectedDebtorId,      setSelectedDebtorId]      = useState(null);
  const [selectedDebtorAddress, setSelectedDebtorAddress] = useState(null);

  // Delete Pre-LBA
  const [deletingLbaId, setDeletingLbaId] = useState(null);

  // ── Initial load ────────────────────────────────────────────────────────────
  useEffect(() => {
    const cid = user?.companyId;
    const uid = user?.id;
    if (cid) {
      setCompanyId(cid);
      setUserName(user?.name);

      debtorDetailsService.fetchUserSubmissionData(uid).then((result) => {
        if (result.success) {
          setTotalSubmitted(result.details.length);
          setLbaDetails(result.details);
        }
      });
    }
  }, []);

  // ── Fetchers — use getCompanyId() / getToken() so they never depend on state timing ──

  const fetchXeroInvoices = useCallback(async () => {
    const cid = getCompanyId();
    if (!cid) return;
    setIsLoadingXero(true);
    try {
      const res  = await fetch(`${API_URL}/api/xero/invoices`, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      setXeroInvoices(data.success ? (data.invoices || []).filter((inv) => inv.xeroPdfS3Key) : []);
    } catch (e) {
      console.error("Xero fetch error:", e);
      setXeroInvoices([]);
    } finally {
      setIsLoadingXero(false);
    }
  }, [API_URL]);

  const fetchSourceInvoices = useCallback(async (source, setter, setLoading) => {
    const cid = getCompanyId();
    if (!cid) return;
    setLoading(true);
    try {
      const res  = await fetch(`${API_URL}/api/documents/invoices/list?source=${source}`, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      setter(data.success ? (data.invoices || []) : []);
    } catch (e) {
      console.error(`${source} fetch error:`, e);
      setter([]);
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  // ── KEY FIX: fetchUploadedInvoices does NOT guard on companyId state ────────
  const fetchUploadedInvoices = useCallback(async () => {
    setIsLoadingUploaded(true);
    try {
      const res  = await fetch(`${API_URL}/api/documents/invoices/uploaded`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      console.log("Uploaded invoices response:", data);   // debug — remove after confirming
      setUploadedInvoices(
        data.success ? (data.invoices || []).map((inv) => ({ ...inv, _isUploaded: true })) : []
      );
    } catch (e) {
      console.error("Uploaded invoices fetch error:", e);
      setUploadedInvoices([]);
    } finally {
      setIsLoadingUploaded(false);
    }
  }, [API_URL]);

  // ── Open modal ──────────────────────────────────────────────────────────────
  // No payment-account gate: a Pre-LBA can be started whether or not a
  // payment link / Stripe Connect account has been set up.
  const onClickDebtorDetails = () => {
    setInvoiceSource("xero");
    setSelectedInvoice(null);
    setSelectedFiles([]);
    setInvoiceSearchQuery("");
    setUploadModalOpen(true);

    // Fire all four fetches immediately — none wait on state
    fetchXeroInvoices();
    fetchUploadedInvoices();
    fetchSourceInvoices("sage",       setSageInvoices, setIsLoadingSage);
    fetchSourceInvoices("quickbooks", setQbInvoices,   setIsLoadingQb);
  };

  // ── Tab switch ──────────────────────────────────────────────────────────────
  const handleTabSwitch = (key) => {
    setInvoiceSource(key);
    setSelectedInvoice(null);
    setInvoiceSearchQuery("");
    if (key === "sage"       && !sageInvoices.length     && !isLoadingSage)
      fetchSourceInvoices("sage",       setSageInvoices, setIsLoadingSage);
    if (key === "quickbooks" && !qbInvoices.length       && !isLoadingQb)
      fetchSourceInvoices("quickbooks", setQbInvoices,   setIsLoadingQb);
    if (key === "uploaded"   && !uploadedInvoices.length && !isLoadingUploaded)
      fetchUploadedInvoices();
  };

  // ── Build LBA form state ────────────────────────────────────────────────────
  const buildConnectedInvoiceState = (inv) => {
    const addr = inv.xeroContactAddress || {};
    return {
      name:                 inv.xeroContactName  || "",
      email:                inv.xeroContactEmail || "",
      companyId:            getCompanyId(),
     // autoIncrementAccount: user?.autoIncrementAccount,
      source:               invoiceSource,
      businessName:         inv.xeroContactName  || "",
      invoiceNumber:        inv.xeroInvoiceNumber || inv.fileName || "",
      invoiceId:            inv._id || inv.id,
      amount:               inv.amountDue || inv.totalAmounts || "",
      currency:             inv.currency  || "GBP",
      dueDate:              inv.dueDate ? new Date(inv.dueDate) : null,
      houseName: addr.house      || addr.line1    || "",
      street:    addr.street     || addr.line2    || "",
      city:      addr.city       || "",
      country:   addr.country    || "United Kingdom",
      postcode:  addr.postalCode || addr.postcode || "",
      xeroInvoiceData: inv,
    };
  };

  // ── Proceed ─────────────────────────────────────────────────────────────────
  const handleAttachFiles = async () => {
    if (selectedInvoice && invoiceSource !== "manual") {
      setUploadModalOpen(false);
      navigate("/lbaform", { state: buildConnectedInvoiceState(selectedInvoice) });
      return;
    }

    if (!selectedFiles.length) { alert("Please select files or choose an invoice before proceeding."); return; }
    const cid = getCompanyId();
    if (!cid) { alert("Company ID is missing!"); return; }

    try {
      setIsUploading(true);
      const result = await documentService.uploadInvoicesAndIncrement(selectedFiles, cid);
      if (result.success) {
        setUploadModalOpen(false);
        const inv = result.invoiceData || {};
        const ext = inv.extractedDetails || {};
        const firstInvoiceNumber = Array.isArray(ext.invoiceNumbers) ? ext.invoiceNumbers[0] : "";
        const firstInvoiceDate   = Array.isArray(ext.invoiceDates)   ? ext.invoiceDates[0]   : "";
        const firstAmount        = Array.isArray(ext.totalAmounts)   ? ext.totalAmounts[0]   : "";
        const contactName        = ext.contactName  || "";
        const contactEmail       = ext.contactEmail || "";
        const address            = ext.address      || {};
        const currency           = ext.currency     || "GBP";

        let parsedDueDate = null;
        const rawDue = ext.dueDate || firstInvoiceDate;
        if (rawDue && rawDue !== "Not found") {
          try { parsedDueDate = new Date(rawDue); if (isNaN(parsedDueDate.getTime())) parsedDueDate = null; }
          catch { parsedDueDate = null; }
        }

        const cleanAmount = firstAmount && firstAmount !== "Not found"
          ? firstAmount.replace(/[£$€,]/g, "").trim() : "";

        navigate("/lbaform", {
          state: {
            name: contactName || userName, email: contactEmail, businessName: contactName,
            companyId: cid, autoIncrementAccount: result.autoIncrementAccount,
            selectedFiles, source: "manual",
            invoiceNumber: firstInvoiceNumber !== "Not found" ? firstInvoiceNumber : "",
            invoiceId: inv._id || inv.id, amount: cleanAmount, dueDate: parsedDueDate, currency,
            houseName: address.house || "", street: address.street || "",
            city: address.city || "", postcode: address.postcode || "",
            country: address.country || "United Kingdom",
          },
        });
      } else {
        alert("There was a problem processing your invoice. Please try again.");
      }
    } catch (error) {
      alert(`Error: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancelUpload = () => {
    setUploadModalOpen(false);
    setSelectedFiles([]);
    setSelectedInvoice(null);
    setInvoiceSource("xero");
    setInvoiceSearchQuery("");
  };

  // ── Delete Pre-LBA ──────────────────────────────────────────────────────────
  // NOTE: endpoint assumed as DELETE /api/lba/:id — confirm against your actual
  // backend route (may live in debtorDetailsService instead of a raw fetch).
  const handleDeleteLba = async (lbaId, name) => {
    if (!window.confirm(`Delete the Pre-LBA for "${name}"? This cannot be undone.`)) return;
    setDeletingLbaId(lbaId);
    try {
      const res = await fetch(`${API_URL}/api/lba/${lbaId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      if (data.success) {
        setLbaDetails(prev => prev.filter(item => item.id !== lbaId));
        setTotalSubmitted(prev => prev - 1);
      } else if (res.status === 400 && data.message?.includes("payment link")) {
        alert(data.message); // guard-specific message, already clear
      } else {
        alert(data.message || "Failed to delete Pre-LBA.");
      }
    } catch (e) {
      alert("Failed to delete Pre-LBA. Please try again.");
    } finally {
      setDeletingLbaId(null);
    }
  };

  // ── Formatters ──────────────────────────────────────────────────────────────
  const formatDate        = (d) => new Date(d).toLocaleDateString("en-GB");
  const formatCurrency    = (amount, currency = "GBP") =>
    new Intl.NumberFormat("en-GB", { style: "currency", currency }).format(amount || 0);
  const formatDisplayDate = (d) => {
    if (!d) return "-";
    return new Date(d).toLocaleDateString("en-GB", { day:"2-digit", month:"short", year:"numeric" });
  };

  // ── Table filtering ─────────────────────────────────────────────────────────
  const filteredItems = lbaDetails.filter((item) => {
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === "all" ||
      (filterStatus === "Settled"   && item.status === "Resolved") ||
      (filterStatus === "Unsettled" && item.status !== "Resolved");
    if (!matchesStatus) return false;
    if (selectedDateTimeRange?.startDate && selectedDateTimeRange?.endDate) {
      const sub = formatDate(item.createdAt);
      return matchesSearch && sub >= selectedDateTimeRange.startDate && sub <= selectedDateTimeRange.endDate;
    }
    return matchesSearch;
  });

  const indexOfLastItem  = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems     = filteredItems.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages       = Math.ceil(filteredItems.length / itemsPerPage);

  const handleSearchChange   = (e) => { setSearchQuery(e.target.value); setCurrentPage(1); };
  const handleDateTimeSelect = (r) => setSelectedDateTimeRange(r);

  // ── Callbacks ───────────────────────────────────────────────────────────────
  const onSeeDetailsTextClick = useCallback(
    (lbaId, name, currency, amount, email, address, userId, companyId, dueDate) => {
      navigate("/lba2", { state: { lbaId, name, currency, amount, email, address, userId, companyId, dueDate } });
    },
    [navigate]
  );
  const openLawFirm             = useCallback((id, addr) => { setSelectedDebtorId(id); setSelectedDebtorAddress(addr); setLawFirmOpen(true); }, []);
  const closeLawFirm            = useCallback(() => setLawFirmOpen(false), []);
  const openDownloadModalTables = useCallback(() => setDownloadModalTablesOpen(true),  []);
  const closeDownloadModalTables= useCallback(() => setDownloadModalTablesOpen(false), []);
  const openDateFilter          = useCallback(() => setDateFilterOpen(true),  []);
  const closeDateFilter         = useCallback(() => setDateFilterOpen(false), []);
  const closeNavigationDropdown = useCallback(() => setNavigationDropdownOpen(false), []);
  const closeLogoutConfirmation = useCallback(() => setLogoutConfirmationOpen(false), []);

  // ── Derived ─────────────────────────────────────────────────────────────────
  const settledCount   = lbaDetails.filter((i) => i.status === "Resolved").length;
  const unsettledCount = lbaDetails.filter((i) => i.status !== "Resolved").length;

  const activeInvoices = {
    xero:       xeroInvoices,
    sage:       sageInvoices,
    quickbooks: qbInvoices,
    uploaded:   uploadedInvoices,
    manual:     [],
  }[invoiceSource] || [];

  const activeLoading = {
    xero:       isLoadingXero,
    sage:       isLoadingSage,
    quickbooks: isLoadingQb,
    uploaded:   isLoadingUploaded,
    manual:     false,
  }[invoiceSource];

  const tabEmptyMessages = {
    xero:       { msg: "No Xero invoices found.",       hint: "Connect Xero in settings to import invoices." },
    sage:       { msg: "No Sage invoices found.",       hint: "Sync Sage invoices in settings first." },
    quickbooks: { msg: "No QuickBooks invoices found.", hint: "Sync QuickBooks invoices in settings first." },
    uploaded:   { msg: "No uploaded invoices found.",   hint: "Go to Invoices → Upload to add PDFs." },
    manual:     { msg: "", hint: "" },
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
        html,body { background:rgb(241,245,249)!important; min-width:1024px; }
        .lba-root*{box-sizing:border-box;}
        .lba-root{font-family:'DM Sans',sans-serif;background:rgb(241,245,249)!important;min-height:100vh;}
        .lba-card{background:#fff;border:1px solid #E2E8F0;border-radius:16px;box-shadow:0 1px 3px rgba(0,0,0,.06),0 2px 12px rgba(0,0,0,.04);transition:box-shadow .2s,transform .2s;position:relative;overflow:hidden;}
        .lba-metric-card{cursor:default;}
        .lba-metric-card:hover{transform:translateY(-3px);box-shadow:0 4px 24px rgba(0,0,0,.09);}
        .lba-metric-card .accent-bar{position:absolute;top:0;left:0;right:0;height:4px;border-radius:16px 16px 0 0;}
        .lba-tab-row{display:inline-flex;background:#F1F5F9;border-radius:8px;padding:3px;gap:2px;}
        .lba-tab{padding:6px 16px;border-radius:6px;font-family:'DM Sans',sans-serif;font-size:13px;font-weight:600;color:#64748B;cursor:pointer;border:none;background:transparent;transition:background .15s,color .15s;}
        .lba-tab:hover{background:#fff;color:#0F172A;}
        .lba-tab.lba-active{background:#fff;color:#2563EB;box-shadow:0 1px 4px rgba(0,0,0,.08);}
        .lba-search{display:flex;align-items:center;gap:8px;background:#fff;border:1px solid #E2E8F0;border-radius:10px;padding:9px 14px;width:220px;transition:border-color .15s,box-shadow .15s;}
        .lba-search:focus-within{border-color:#2563EB;box-shadow:0 0 0 3px rgba(37,99,235,.1);}
        .lba-search input{border:none;outline:none;background:transparent;font-family:'DM Sans',sans-serif;font-size:14px;color:#0F172A;width:100%;}
        .lba-search input::placeholder{color:#94A3B8;}
        .lba-table{width:100%;border-collapse:collapse;}
        .lba-table thead tr{background:#F8FAFC;}
        .lba-table th{padding:11px 16px;text-align:left;font-size:11px;font-weight:700;color:#94A3B8;text-transform:uppercase;letter-spacing:.07em;border-bottom:1px solid #E2E8F0;}
        .lba-table td{padding:14px 16px;font-size:14px;color:#1E293B;border-bottom:1px solid #F1F5F9;vertical-align:middle;}
        .lba-table tbody tr{transition:background .12s;}
        .lba-table tbody tr:hover{background:#F8FAFC;}
        .lba-table tbody tr:last-child td{border-bottom:none;}
        .lba-badge{display:inline-flex;align-items:center;gap:5px;padding:3px 10px;border-radius:20px;font-size:12px;font-weight:600;}
        .lba-page-btn{display:inline-flex;align-items:center;gap:6px;padding:7px 16px;background:#fff;border:1px solid #E2E8F0;border-radius:8px;font-family:'DM Sans',sans-serif;font-size:13px;font-weight:600;color:#0F172A;cursor:pointer;transition:border-color .15s,background .15s,color .15s;}
        .lba-page-btn:hover:not(:disabled){border-color:#2563EB;background:#EFF6FF;color:#2563EB;}
        .lba-page-btn:disabled{opacity:.4;cursor:not-allowed;}
        .lba-dl-btn{display:inline-flex;align-items:center;gap:6px;padding:8px 16px;background:#fff;border:1px solid #E2E8F0;border-radius:8px;font-family:'DM Sans',sans-serif;font-size:13px;font-weight:600;color:#1E293B;cursor:pointer;transition:border-color .15s,background .15s,color .15s;}
        .lba-dl-btn:hover{border-color:#2563EB;background:#EFF6FF;color:#2563EB;}
        .lba-count-pill{display:inline-flex;align-items:center;gap:5px;padding:3px 10px;background:#EFF6FF;border-radius:20px;font-size:12px;font-weight:700;color:#2563EB;}
        .lba-new-btn{display:inline-flex;align-items:center;gap:8px;padding:9px 18px;border-radius:10px;border:none;font-family:'DM Sans',sans-serif;font-size:14px;font-weight:700;cursor:pointer;transition:opacity .15s,transform .15s;background:linear-gradient(135deg,#2563EB 0%,#1D4ED8 100%);color:#fff;box-shadow:0 2px 8px rgba(37,99,235,.3);}
        .lba-new-btn:hover{opacity:.9;transform:translateY(-1px);}
        .lba-link{color:#2563EB;font-weight:600;font-size:13px;cursor:pointer;text-decoration:none;}
        .lba-link:hover{text-decoration:underline;}
        .lba-name-cell{font-weight:600;color:#0F172A;}
        .lba-amount-cell{font-family:'DM Mono',monospace;font-size:13px;}
        .lba-delete-btn{background:none;border:none;cursor:pointer;color:#DC2626;font-size:13px;font-weight:600;display:inline-flex;align-items:center;gap:4px;font-family:'DM Sans',sans-serif;padding:0;}
        .lba-delete-btn:hover:not(:disabled){text-decoration:underline;}
        .lba-delete-btn:disabled{opacity:.5;cursor:not-allowed;}

        /* ── Modal ── */
        .lba-modal{background:#fff;border-radius:16px;width:580px;max-width:96vw;max-height:90vh;display:flex;flex-direction:column;border:1px solid #E2E8F0;box-shadow:0 20px 60px rgba(0,0,0,.15);}
        .lba-modal-header{padding:20px 24px 0;border-bottom:1px solid #E2E8F0;padding-bottom:0;}
        .lba-modal-body{flex:1;overflow-y:auto;padding:20px 24px;}
        .lba-modal-footer{padding:16px 24px;border-top:1px solid #E2E8F0;display:flex;justify-content:flex-end;gap:10px;background:#FAFBFD;border-radius:0 0 16px 16px;}

        /* source tabs */
        .lba-source-tabs{display:flex;gap:0;overflow-x:auto;margin-top:16px;}
        .lba-source-tab{padding:11px 18px;font-family:'DM Sans',sans-serif;font-size:13px;font-weight:600;color:#64748B;white-space:nowrap;cursor:pointer;border:none;background:transparent;border-bottom:2px solid transparent;margin-bottom:-1px;transition:color .15s,border-color .15s;flex-shrink:0;}
        .lba-source-tab:hover{color:#0F172A;}
        .lba-source-tab.active{color:#2563EB;border-bottom-color:#2563EB;}
        .lba-source-tab.uploaded-active{color:#7C3AED;border-bottom-color:#7C3AED;}

        .lba-invoice-item{padding:12px 14px;border:1px solid #E2E8F0;border-radius:10px;cursor:pointer;transition:border-color .15s,background .15s;margin-bottom:8px;}
        .lba-invoice-item:hover{border-color:#93C5FD;background:#F8FAFC;}
        .lba-invoice-item.selected{border-color:#2563EB;background:#EFF6FF;}

        .lba-xero-search{display:flex;align-items:center;gap:8px;background:#F8FAFC;border:1px solid #E2E8F0;border-radius:8px;padding:8px 12px;margin-bottom:12px;transition:border-color .15s;}
        .lba-xero-search:focus-within{border-color:#2563EB;background:#fff;}
        .lba-xero-search input{border:none;outline:none;background:transparent;font-family:'DM Sans',sans-serif;font-size:13px;color:#0F172A;width:100%;}

        .lba-selection-banner{margin-top:14px;padding:10px 14px;background:#F0FDF4;border:1px solid #BBF7D0;border-radius:8px;font-size:13px;color:#166534;}
        .lba-selection-banner.purple{background:#F5F3FF;border-color:#DDD6FE;color:#6D28D9;}

        .lba-cancel-btn{padding:9px 18px;border-radius:8px;border:1px solid #E2E8F0;background:#fff;font-family:'DM Sans',sans-serif;font-size:14px;font-weight:600;color:#0F172A;cursor:pointer;}
        .lba-cancel-btn:hover{background:#F8FAFC;}
        .lba-proceed-btn{padding:9px 20px;border-radius:8px;border:none;background:#2563EB;color:#fff;font-family:'DM Sans',sans-serif;font-size:14px;font-weight:700;cursor:pointer;display:inline-flex;align-items:center;gap:8px;transition:background .15s;}
        .lba-proceed-btn:hover:not(:disabled){background:#1D4ED8;}
        .lba-proceed-btn:disabled{background:#93C5FD;cursor:not-allowed;}

        .uploaded-hint{display:flex;align-items:flex-start;gap:10px;padding:10px 14px;background:#F5F3FF;border:1px solid #DDD6FE;border-radius:8px;font-size:12px;color:#6D28D9;margin-bottom:14px;line-height:1.5;}
        .tab-count-badge{display:inline-flex;align-items:center;justify-content:center;min-width:18px;height:18px;padding:0 5px;border-radius:9px;font-size:11px;font-weight:700;margin-left:5px;}

        .lba-spinner{display:inline-block;width:16px;height:16px;border:2px solid #E2E8F0;border-top-color:#2563EB;border-radius:50%;animation:lba-spin .7s linear infinite;flex-shrink:0;}
        @keyframes lba-spin{to{transform:rotate(360deg);}}
      `}</style>

      <div className="lba-root w-full overflow-visible">
        <NavigationPanel />

        <div style={{ paddingTop:136, paddingBottom:64, paddingLeft:96, paddingRight:96, display:"flex", flexDirection:"column", gap:28, minWidth:0 }}>

          {/* ── Page header ── */}
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", minWidth:0 }}>
            <div>
              <h1 style={{ margin:0, fontSize:22, fontWeight:700, color:"#0F172A" }}>
                Pre-Letter Before Action (LBA)
              </h1>
              <p style={{ margin:"4px 0 0", fontSize:14, color:"#64748B" }}>
                All information regarding your pre-LBA.
              </p>
            </div>
            <button
              className="lba-new-btn"
              onClick={onClickDebtorDetails}
              style={{ marginLeft:24, flexShrink:0 }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M9 22h6c5 0 7-2 7-7V9c0-5-2-7-7-7H9C4 2 2 4 2 9v6c0 5 2 7 7 7ZM15.75 9h-7.5M15.75 15h-7.5"
                  stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              New Pre-LBA
            </button>
          </div>

          {/* ── Metric cards ── */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:18 }}>
            {[
              { label:"Total submitted",   value:totalSubmitted, accentBar:"#7C3AED", iconBg:"#F5F3FF",
                icon:<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M15.24 2H8.76C5 2 4.71 5.38 6.74 7.22l10.52 9.56C19.29 18.62 19 22 15.24 22H8.76C5 22 4.71 18.62 6.74 16.78l10.52-9.56C19.29 5.38 19 2 15.24 2Z" stroke="#7C3AED" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg> },
              { label:"Settled Pre-LBA",   value:settledCount,   accentBar:"#059669", iconBg:"#ECFDF5",
                icon:<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M12 22c5.5 0 10-4.5 10-10S17.5 2 12 2 2 6.5 2 12s4.5 10 10 10Z" stroke="#059669" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="m7.75 12 2.83 2.83 5.67-5.66" stroke="#059669" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg> },
              { label:"Unsettled Pre-LBA", value:unsettledCount, accentBar:"#F59E0B", iconBg:"#FFFBEB",
                icon:<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M12 9v5M12 21.41H5.94c-3.47 0-4.92-2.48-3.24-5.51l3.12-5.62L8.76 5c1.78-3.21 4.7-3.21 6.48 0l2.94 5.29 3.12 5.62c1.68 3.03.22 5.51-3.24 5.51H12v-.01Z" stroke="#D97706" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M11.995 17h.009" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg> },
            ].map((card) => (
              <div key={card.label} className="lba-card lba-metric-card" style={{ padding:24 }}>
                <div className="accent-bar" style={{ background:card.accentBar }} />
                <div style={{ marginTop:8 }}>
                  <div style={{ width:40, height:40, borderRadius:10, background:card.iconBg, display:"flex", alignItems:"center", justifyContent:"center", marginBottom:18, border:"1px solid #E2E8F0" }}>{card.icon}</div>
                  <div style={{ fontSize:12, fontWeight:600, color:"#94A3B8", textTransform:"uppercase", letterSpacing:".06em", marginBottom:6 }}>{card.label}</div>
                  <div style={{ fontFamily:"'DM Mono',monospace", fontSize:28, fontWeight:500, color:"#0F172A", lineHeight:1.1 }}>{card.value}</div>
                </div>
              </div>
            ))}
          </div>

          {/* ── Submissions table ── */}
          <div className="lba-card" style={{ overflow:"hidden" }}>

            {/* Table header row */}
            <div style={{ padding:"18px 24px", borderBottom:"1px solid #E2E8F0", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <span style={{ fontSize:16, fontWeight:700, color:"#0F172A" }}>Submitted Pre-LBA's</span>
                <span className="lba-count-pill">
                  <span style={{ width:6, height:6, borderRadius:"50%", background:"#2563EB", display:"inline-block" }} />
                  {filteredItems.length === 1 ? "1 entry" : `${filteredItems.length || "No"} entries`}
                </span>
              </div>
              <button className="lba-dl-btn" onClick={openDownloadModalTables}>
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none">
                  <path d="M9 11v6l2-2M9 17l-2-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M22 10v5c0 5-2 7-7 7H9c-5 0-7-2-7-7V9c0-5 2-7 7-7h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M22 10h-4c-3 0-4-1-4-4V2l8 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Download
              </button>
            </div>

            {/* Filter / search row — two-row on narrow screens */}
            <div style={{ padding:"12px 24px", borderBottom:"1px solid #F1F5F9", background:"#FAFBFD" }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, flexWrap:"wrap" }}>
                <div className="lba-tab-row" style={{ flexShrink:0 }}>
                  {[{key:"all",label:"View all"},{key:"Settled",label:"Settled"},{key:"Unsettled",label:"Unsettled"}].map((tab) => (
                    <button
                      key={tab.key}
                      className={`lba-tab${filterStatus===tab.key?" lba-active":""}`}
                      onClick={() => { setFilterStatus(tab.key); setCurrentPage(1); }}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:10, flexShrink:0 }}>
                  <div className="lba-search">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M11.5 21a9.5 9.5 0 1 0 0-19 9.5 9.5 0 0 0 0 19ZM22 22l-2-2" stroke="#94A3B8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <input type="text" placeholder="Search your Pre-LBA…" value={searchQuery} onChange={handleSearchChange} />
                  </div>
                  <button className="lba-dl-btn" onClick={openDateFilter}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none">
                      <path d="M3 7h18M6 12h12M10 17h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                    Filter
                  </button>
                </div>
              </div>
            </div>

            {/* Table */}
            <div style={{ overflowX:"auto" }}>
              <table className="lba-table" style={{ minWidth:800 }}>
                <thead>
                  <tr>
                    {["Debtor / Company","Invoice no.","Status","Amount due","Submission date","Action","Further action",""].map((h) => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length > 0 ? currentItems.map((item, index) => (
                    <tr key={item.id || index}>
                      {(() => {
                        const company = item.businessName || item.companyName;
                        const showCompany = company && company !== item.name;
                        return (
                          <td>
                            <div className="lba-name-cell">{item.name}</div>
                            {showCompany && (
                              <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>{company}</div>
                            )}
                          </td>
                        );
                      })()}
                      <td className="lba-amount-cell">{item.invoiceNumber || item.invoiceNo || "—"}</td>
                      <td>
                        <span className="lba-badge" style={item.status==="Resolved"?{background:"#F0FDF4",color:"#166534"}:{background:"#FEF2F2",color:"#991B1B"}}>
                          ● {item.status==="Resolved"?"Settled":"Unsettled"}
                        </span>
                      </td>
                      <td className="lba-amount-cell">{item.currency} {(item.amount||0).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                      <td style={{color:"#64748B",fontSize:13,whiteSpace:"nowrap"}}>{formatDate(item.createdAt)}</td>
                      <td><a className="lba-link" href="#" onClick={(e)=>{e.preventDefault();onSeeDetailsTextClick(item.id,item.name,item.currency,item.amount,item.email,item.address,item.userId,item.companyId,item.dueDate);}}>See details</a></td>
                      <td><a className="lba-link" href="#" onClick={(e)=>{e.preventDefault();openLawFirm(item.id,item.address);}} style={{whiteSpace:"nowrap"}}>Contact law firm</a></td>
                      <td>
                        <button
                          className="lba-delete-btn"
                          onClick={() => handleDeleteLba(item.id, item.name)}
                          disabled={deletingLbaId === item.id}
                        >
                          {deletingLbaId === item.id ? (
                            <div className="lba-spinner" style={{ width:13, height:13, borderColor:"#FECACA", borderTopColor:"#DC2626" }} />
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none">
                              <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                          Delete
                        </button>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan="8" style={{textAlign:"center",color:"#94A3B8",padding:40}}>No entries found</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 24px", borderTop:"1px solid #E2E8F0", background:"#FAFBFD" }}>
              <button className="lba-page-btn" disabled={currentPage===1} onClick={()=>setCurrentPage(p=>p-1)}>← Previous</button>
              <span style={{fontSize:13,color:"#64748B",fontWeight:500}}>Page {totalPages>0?currentPage:0} of {totalPages}</span>
              <button className="lba-page-btn" disabled={currentPage===totalPages||totalPages===0} onClick={()=>setCurrentPage(p=>p+1)}>Next →</button>
            </div>
          </div>

          <Footer />
        </div>
      </div>

      {/* ── Invoice picker modal ── */}
      {isUploadModalOpen && (
        <PortalPopup overlayColor="rgba(113,113,113,0.3)" placement="Centered" onOutsideClick={isUploading ? null : handleCancelUpload}>
          <div className="lba-modal">

            {/* Header */}
            <div className="lba-modal-header">
              <div style={{ padding:"0 0 0 0" }}>
                <h2 style={{ margin:0, fontSize:18, fontWeight:700, color:"#0F172A" }}>Start New Pre-LBA</h2>
                <p style={{ margin:"4px 0 0", fontSize:13, color:"#64748B" }}>
                  Pick an invoice from your accounting software, your uploaded invoices, or upload a new one manually.
                </p>
              </div>

              {/* Source tabs */}
              <div className="lba-source-tabs">
                {SOURCE_TABS.map((tab) => {
                  const isActive   = invoiceSource === tab.key;
                  const isUploaded = tab.key === "uploaded";
                  return (
                    <button
                      key={tab.key}
                      className={`lba-source-tab${isActive ? (isUploaded ? " uploaded-active" : " active") : ""}`}
                      onClick={() => handleTabSwitch(tab.key)}
                    >
                      {tab.label}
                      {isUploaded && (
                        <span className="tab-count-badge" style={{
                          background: isActive ? "#EDE9FE" : "#F1F5F9",
                          color:      isActive ? "#7C3AED" : "#64748B",
                        }}>
                          {isLoadingUploaded ? "…" : uploadedInvoices.length}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Body */}
            <div className="lba-modal-body">

              {/* Uploaded tab hint */}
              {invoiceSource === "uploaded" && !isLoadingUploaded && uploadedInvoices.length > 0 && (
                <div className="uploaded-hint">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" style={{flexShrink:0,marginTop:1}}>
                    <path d="M12 22c5.5 0 10-4.5 10-10S17.5 2 12 2 2 6.5 2 12s4.5 10 10 10ZM12 8v5M11.995 16h.01" stroke="#7C3AED" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  These are PDFs you've already uploaded to the system. Select one to pre-fill the LBA form with its extracted details — no re-upload needed.
                </div>
              )}

              {/* Picker for all connected + uploaded tabs */}
              {invoiceSource !== "manual" && (
                <InvoicePickerTab
                  invoices={activeInvoices}
                  isLoading={activeLoading}
                  searchQuery={invoiceSearchQuery}
                  onSearchChange={setInvoiceSearchQuery}
                  selectedInvoice={selectedInvoice}
                  onSelect={setSelectedInvoice}
                  emptyMessage={tabEmptyMessages[invoiceSource].msg}
                  emptyHint={tabEmptyMessages[invoiceSource].hint}
                  formatCurrency={formatCurrency}
                  formatDisplayDate={formatDisplayDate}
                />
              )}

              {/* Manual upload */}
              {invoiceSource === "manual" && (
                <div>
                  <div style={{ fontSize:14, fontWeight:700, color:"#0F172A", marginBottom:8 }}>Upload invoice documents</div>
                  <div style={{
                    border:`1px solid ${selectedFiles.length>0?"#2563EB":"#E2E8F0"}`,
                    borderRadius:10, background:selectedFiles.length>0?"#EFF6FF":"#F8FAFC",
                    padding:12, transition:"border-color .15s,background .15s",
                  }}>
                    <Upload onFilesSelected={(files) => setSelectedFiles(files)} width="100%" />
                    {selectedFiles.length > 0 && (
                      <div style={{ marginTop:8, fontSize:13, color:"#2563EB", fontWeight:600 }}>
                        {selectedFiles.length} file(s) selected
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Selection banner */}
              {(selectedInvoice || (invoiceSource === "manual" && selectedFiles.length > 0)) && (
                <div className={`lba-selection-banner${invoiceSource==="uploaded"?" purple":""}`}>
                  <strong>Selected:</strong>{" "}
                  {selectedInvoice
                    ? `${SOURCE_TABS.find((t)=>t.key===invoiceSource)?.label} Invoice — ${selectedInvoice.xeroInvoiceNumber||selectedInvoice.fileName||"No number"}${selectedInvoice.xeroContactName?` · ${selectedInvoice.xeroContactName}`:""}`
                    : `${selectedFiles.length} file(s) for manual upload`}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="lba-modal-footer">
              <button className="lba-cancel-btn" onClick={handleCancelUpload} disabled={isUploading}>Cancel</button>
              <button
                className="lba-proceed-btn"
                onClick={handleAttachFiles}
                disabled={isUploading || (invoiceSource==="manual" ? selectedFiles.length===0 : !selectedInvoice)}
              >
                {isUploading ? <><span>Processing…</span><div className="lba-spinner" style={{borderColor:"rgba(255,255,255,.3)",borderTopColor:"#fff"}} /></> : "Proceed →"}
              </button>
            </div>
          </div>
        </PortalPopup>
      )}

      {/* Other modals */}
      {isNavigationDropdownOpen && (
        <PortalPopup overlayColor="rgba(113,113,113,0.3)" placement="Centered" onOutsideClick={closeNavigationDropdown}>
          <NavigationDropdown onClose={closeNavigationDropdown} />
        </PortalPopup>
      )}
      {isLogoutConfirmationOpen && (
        <PortalPopup overlayColor="rgba(113,113,113,0.3)" placement="Centered" onOutsideClick={closeLogoutConfirmation}>
          <LogoutConfirmation onClose={closeLogoutConfirmation} />
        </PortalPopup>
      )}
      {isDownloadModalTablesOpen && (
        <PortalPopup overlayColor="rgba(113,113,113,0.3)" placement="Centered" onOutsideClick={closeDownloadModalTables}>
          <DownloadModalTables onClose={closeDownloadModalTables} tableData={filteredItems} />
        </PortalPopup>
      )}
      {isDateFilterOpen && (
        <PortalPopup overlayColor="rgba(113,113,113,0.3)" placement="Centered" onOutsideClick={closeDateFilter}>
          <DateFilter onClose={closeDateFilter} onDateTimeSelect={handleDateTimeSelect} />
        </PortalPopup>
      )}
      {isLawFirmOpen && (
        <PortalPopup overlayColor="rgba(113,113,113,0.3)" placement="Centered" onOutsideClick={closeLawFirm}>
          <LawFirm onClose={closeLawFirm} debtorId={selectedDebtorId} formType="lba" debtorAddress={selectedDebtorAddress} />
        </PortalPopup>
      )}
    </>
  );
};

export default LBA;